// QuizApp.tsx - Complete implementation
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  BookOpen,
  Sparkles,
  Users,
  GraduationCap,
  ChevronDown,
  Award,
  UserPlus,
  User,
  Mail,
  Phone,
  Calendar,
  Plus,
  X,
  TrendingUp,
  Lightbulb,
  CheckCircle,
  XCircle,
  ArrowLeft,
  ArrowRight
} from 'lucide-react';
import { MyFlexWrapBox } from './components/MyFlexWrapBox';
import { MyDialogButton } from './components/MyDialogButton';
import { MyBox } from './components/MyBox';
import FullScreen from './components/FullScreen';

// ============================================================
// TYPES
// ============================================================
export interface Question {
  question: string;
  options: string[];
  correct: number;
  explanation?: string;
}

export interface QuizTopic {
  id: string;
  title: string;
  icon: string;
  color: string;
  description: string;
  questions: Question[];
  passingPercentage: number;
}

export interface QuizResult {
  score: number;
  passed: boolean;
  correctAnswers: number;
  totalQuestions: number;
  answers: number[];
}

export interface Student {
  id: string;
  name: string;
  grade: string;
  section?: string;
  email?: string;
  phone?: string;
  enrollmentDate: string;
}

export interface StudentWithGrade {
  studentId: string;
  studentName: string;
  grade: string;
  topicId: string;
  score: number;
  passed: boolean;
  completedAt: string;
  correctAnswers: number;
  totalQuestions: number;
}

export interface QuizAppData {
  app: {
    id: string;
    name: string;
    version: string;
    logo: string;
    hero: {
      title: string;
      subtitle: string;
      tag: string;
      backgroundImage: string;
      buttons: Array<{
        label: string;
        url: string;
        style: 'primary' | 'secondary';
      }>;
    };
    features: Array<{
      id: string;
      icon: string;
      label: string;
      description: string;
    }>;
    topics: QuizTopic[];
  };
}

// ============================================================
// HELPERS
// ============================================================
const generateId = () => `STU-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`;

const ICON_MAP: Record<string, React.ReactNode> = {
  Award: <Award size={20} />,
  TrendingUp: <TrendingUp size={20} />,
  Lightbulb: <Lightbulb size={20} />,
  Users: <Users size={20} />,
};

const getIcon = (iconName: string): React.ReactNode => ICON_MAP[iconName] || null;

// ============================================================
// HERO SECTION
// ============================================================
interface HeroSectionProps {
  config: {
    name: string;
    logo: string;
    hero: {
      title: string;
      subtitle: string;
      tag: string;
      backgroundImage: string;
      buttons: Array<{
        label: string;
        url: string;
        style: 'primary' | 'secondary';
      }>;
    };
    features: Array<{
      id: string;
      icon: string;
      label: string;
      description: string;
    }>;
  };
}

const HeroSection = ({ config }: HeroSectionProps) => (
  <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10 pb-6">
    <div className="relative overflow-hidden rounded-2xl sm:rounded-[2rem] bg-gradient-to-r from-indigo-900 via-purple-900 to-indigo-900 text-white shadow-xl">
      <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top_right,white,transparent_60%)]" />
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_bottom_left,white,transparent_50%)]" />
      
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 p-6 sm:p-10 md:p-14">
        <div className="flex items-center gap-3 mb-6">
          <span className="text-3xl">{config.logo}</span>
          <span className="text-xl font-bold tracking-tight">{config.name}</span>
        </div>

        <h1 className="text-2xl sm:text-4xl md:text-6xl font-black leading-tight mb-4 sm:mb-5">
          {config.hero.title}
        </h1>

        <p className="text-sm sm:text-base md:text-lg text-white/80 leading-relaxed max-w-2xl">
          {config.hero.subtitle}
        </p>

        <div className="mt-6 sm:mt-8 flex flex-wrap gap-3">
          {config.hero.buttons.map((button, index) => (
            <a
              key={index}
              href={button.url}
              className={`h-11 sm:h-12 px-5 sm:px-6 rounded-xl font-semibold transition flex items-center gap-2 ${
                button.style === 'primary'
                  ? 'bg-white text-indigo-900 hover:bg-white/90'
                  : 'bg-white/10 backdrop-blur hover:bg-white/15 text-white'
              }`}
            >
              {button.label}
            </a>
          ))}
        </div>

        <div className="mt-8 sm:mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {config.features.map((feature) => (
            <div key={feature.id} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-white/10 backdrop-blur flex items-center justify-center">
                {getIcon(feature.icon)}
              </div>
              <div>
                <p className="text-xs sm:text-sm font-semibold">{feature.label}</p>
                <p className="text-xs text-white/60 hidden sm:block">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// ============================================================
// TOPIC CARD
// ============================================================
interface TopicCardProps {
  topic: QuizTopic;
  isSelected: boolean;
  onSelect: () => void;
  onStart: () => void;
  studentGrade?: StudentWithGrade;
  onViewCertificate?: () => void;
}

const TopicCard = ({
  topic,
  isSelected,
  onSelect,
  onStart,
  studentGrade,
  onViewCertificate,
}: TopicCardProps) => (
  <div
    className={`bg-white rounded-2xl shadow-sm border-2 transition-all duration-300 overflow-hidden ${
      isSelected ? 'border-indigo-500 shadow-lg' : 'border-gray-100 hover:border-indigo-300'
    }`}
  >
    <div className={`h-2 bg-gradient-to-r ${topic.color}`} />
    
    <div className="p-6">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{topic.icon}</span>
          <div>
            <h3 className="font-semibold text-gray-800 text-lg">{topic.title}</h3>
            <p className="text-xs text-gray-500">{topic.questions.length} questions</p>
          </div>
        </div>
        {studentGrade && (
          <div className={`px-2 py-1 rounded-lg text-xs font-bold ${
            studentGrade.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {studentGrade.passed ? '✓ Passed' : '✗ Failed'}
          </div>
        )}
      </div>

      <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-2">
        {topic.description}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={onStart}
          className="flex-1 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition text-sm font-medium"
        >
          Start Quiz
        </button>
        
        {onViewCertificate && (
          <button
            onClick={onViewCertificate}
            className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:shadow-lg transition text-sm font-medium flex items-center gap-1"
          >
            <Award className="w-4 h-4" />
            Certificate
          </button>
        )}
      </div>
    </div>
  </div>
);

// ============================================================
// QUIZ NAVBAR
// ============================================================
interface QuizNavbarProps {
  selectedTopic: QuizTopic;
  studentName: string;
  onChangeTopic: () => void;
}

const QuizNavbar = ({
  selectedTopic,
  studentName,
  onChangeTopic,
}: QuizNavbarProps) => (
  <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
    <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <span className="text-2xl">{selectedTopic.icon}</span>
        <div>
          <h1 className="font-semibold text-gray-800 text-sm sm:text-base">{selectedTopic.title}</h1>
          <p className="text-xs text-gray-500">Student: {studentName}</p>
        </div>
      </div>
      <button
        onClick={onChangeTopic}
        className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-xl transition text-gray-700 font-medium"
      >
        ← Change Topic
      </button>
    </div>
  </div>
);

// ============================================================
// QUIZ COMPONENT
// ============================================================
interface QuizProps {
  title: string;
  description: string;
  studentName: string;
  questions: Question[];
  passingPercentage: number;
  primaryColor: string;
  showExplanation: boolean;
  onComplete: (result: QuizResult) => void;
}

const Quiz = ({
  title,
  questions,
  passingPercentage,
  primaryColor,
  showExplanation,
  onComplete,
}: QuizProps) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>(new Array(questions.length).fill(-1));
  const [showResults, setShowResults] = useState(false);

  const handleAnswer = (answerIndex: number) => {
    if (selectedAnswers[currentQuestion] !== -1) return;
    const newAnswers = [...selectedAnswers];
    newAnswers[currentQuestion] = answerIndex;
    setSelectedAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      finishQuiz();
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) setCurrentQuestion(currentQuestion - 1);
  };

  const finishQuiz = () => {
    const correct = selectedAnswers.filter(
      (answer, index) => answer === questions[index].correct
    ).length;
    const score = Math.round((correct / questions.length) * 100);
    const passed = score >= passingPercentage;

    onComplete({
      score,
      passed,
      correctAnswers: correct,
      totalQuestions: questions.length,
      answers: selectedAnswers,
    });
    setShowResults(true);
  };

  const restartQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswers(new Array(questions.length).fill(-1));
    setShowResults(false);
  };

  if (showResults) {
    const correct = selectedAnswers.filter(
      (answer, index) => answer === questions[index].correct
    ).length;
    const score = Math.round((correct / questions.length) * 100);
    const passed = score >= passingPercentage;

    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className={`text-6xl mb-4 ${passed ? 'text-green-500' : 'text-red-500'}`}>
            {passed ? '🎉' : '😅'}
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {passed ? 'Congratulations!' : 'Keep Learning!'}
          </h2>
          <p className="text-gray-600 mb-6">
            {passed 
              ? `You passed the ${title} quiz with ${score}%!` 
              : `You scored ${score}%. ${passingPercentage}% is required to pass.`
            }
          </p>
          
          <div className="flex justify-center gap-8 mb-6">
            <div>
              <p className="text-3xl font-bold text-indigo-600">{score}%</p>
              <p className="text-sm text-gray-500">Score</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-indigo-600">{correct}/{questions.length}</p>
              <p className="text-sm text-gray-500">Correct</p>
            </div>
          </div>

          <button
            onClick={restartQuiz}
            className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition font-medium"
          >
            Retry Quiz
          </button>
        </div>
      </div>
    );
  }

  const question = questions[currentQuestion];
  const isAnswered = selectedAnswers[currentQuestion] !== -1;
  const isLast = currentQuestion === questions.length - 1;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Progress */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>Question {currentQuestion + 1} of {questions.length}</span>
            <span>{Math.round(((currentQuestion + 1) / questions.length) * 100)}%</span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full mt-2">
            <div
              className={`h-2 bg-gradient-to-r ${primaryColor} rounded-full transition-all duration-300`}
              style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Question */}
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-6">
            {question.question}
          </h3>

          <div className="space-y-3">
            {question.options.map((option, index) => {
              const isSelected = selectedAnswers[currentQuestion] === index;
              const isCorrect = index === question.correct;
              let className = 'w-full text-left px-4 py-3 rounded-xl border-2 transition-all duration-200 ';
              
              if (isSelected) {
                className += isCorrect 
                  ? 'border-green-500 bg-green-50 text-green-700' 
                  : 'border-red-500 bg-red-50 text-red-700';
              } else if (isAnswered && isCorrect) {
                className += 'border-green-300 bg-green-50/50';
              } else {
                className += 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/50';
              }

              return (
                <button
                  key={index}
                  onClick={() => handleAnswer(index)}
                  disabled={isAnswered}
                  className={className}
                >
                  <span className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-500">{String.fromCharCode(65 + index)}.</span>
                    {option}
                    {isAnswered && index === question.correct && ' ✅'}
                    {isSelected && !isCorrect && ' ❌'}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Explanation */}
          {showExplanation && isAnswered && question.explanation && (
            <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
              <p className="text-sm font-medium text-blue-800">📖 Explanation:</p>
              <p className="text-sm text-blue-700 mt-1">{question.explanation}</p>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-6 pt-4 border-t border-gray-100">
            <button
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              className={`px-4 py-2 rounded-xl font-medium transition ${
                currentQuestion === 0
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ArrowLeft className="w-4 h-4 inline mr-1" /> Previous
            </button>
            <button
              onClick={handleNext}
              disabled={!isAnswered}
              className={`px-6 py-2 rounded-xl font-medium transition ${
                isAnswered
                  ? `bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg`
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isLast ? 'Submit ✅' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// CERTIFICATE COMPONENT
// ============================================================
interface CertificateProps {
  title: string;
  studentName: string;
  percentage: number;
  grade: string;
  topicIcon: string;
  certificateId: string;
  issueDate: string;
  passed: boolean;
  correctAnswers: number;
  totalQuestions: number;
}

const Certificate = ({
  title,
  studentName,
  percentage,
  grade,
  topicIcon,
  certificateId,
  issueDate,
  passed,
  correctAnswers,
  totalQuestions,
}: CertificateProps) => {
  if (!passed) return null;

  return (
    <div className="relative bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl p-8 border-4 border-double border-indigo-300 max-w-md mx-auto">
      <div className="absolute inset-2 border-2 border-indigo-200 rounded-xl pointer-events-none" />
      
      <div className="text-center relative">
        <div className="text-5xl mb-3">{topicIcon}</div>
        <h2 className="text-2xl font-bold text-gray-800">Certificate of Achievement</h2>
        <div className="w-20 h-1 bg-gradient-to-r from-indigo-600 to-purple-600 mx-auto my-3" />
        
        <p className="text-sm text-gray-500">This certifies that</p>
        <p className="text-xl font-bold text-indigo-700 my-2">{studentName}</p>
        <p className="text-sm text-gray-500">has successfully completed the</p>
        <p className="text-lg font-semibold text-gray-800 my-2">{title}</p>
        
        <div className="flex justify-center gap-6 my-4">
          <div>
            <p className="text-3xl font-bold text-indigo-600">{percentage}%</p>
            <p className="text-xs text-gray-500">Score</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-green-600">{correctAnswers}/{totalQuestions}</p>
            <p className="text-xs text-gray-500">Correct</p>
          </div>
        </div>

        <div className="flex justify-between text-xs text-gray-400 mt-4 pt-4 border-t border-gray-200">
          <span>ID: {certificateId}</span>
          <span>Grade: {grade}</span>
          <span>Date: {new Date(issueDate).toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// QUIZ HISTORY
// ============================================================
interface QuizHistoryProps {
  topics: QuizTopic[];
  students: Student[];
  className?: string;
  storageKey: string;
  externalHistory?: StudentWithGrade[];
  onHistoryChange?: (history: StudentWithGrade[]) => void;
  onViewCertificate?: (record: StudentWithGrade) => void;
}

const QuizHistory = ({
  topics,
  students,
  className = '',
  externalHistory,
  onViewCertificate,
}: QuizHistoryProps) => {
  const history = externalHistory || [];

  const getTopicTitle = (topicId: string) => {
    return topics.find(t => t.id === topicId)?.title || 'Unknown Topic';
  };

  const getStudentName = (studentId: string) => {
    return students.find(s => s.id === studentId)?.name || 'Unknown Student';
  };

  if (history.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No quiz history yet. Complete a quiz to see results here!</p>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 font-semibold text-gray-600">Student</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-600">Topic</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-600">Score</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-600">Status</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-600">Date</th>
              {onViewCertificate && (
                <th className="text-left py-3 px-4 font-semibold text-gray-600">Certificate</th>
              )}
            </tr>
          </thead>
          <tbody>
            {history.map((record, index) => (
              <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition">
                <td className="py-3 px-4 font-medium text-gray-800">{getStudentName(record.studentId)}</td>
                <td className="py-3 px-4 text-gray-600">{getTopicTitle(record.topicId)}</td>
                <td className="py-3 px-4 font-semibold">{record.score}%</td>
                <td className="py-3 px-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    record.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {record.passed ? '✅ Passed' : '❌ Failed'}
                  </span>
                </td>
                <td className="py-3 px-4 text-gray-500">{new Date(record.completedAt).toLocaleDateString()}</td>
                {onViewCertificate && record.passed && (
                  <td className="py-3 px-4">
                    <button
                      onClick={() => onViewCertificate(record)}
                      className="text-indigo-600 hover:text-indigo-700 font-medium text-xs flex items-center gap-1"
                    >
                      <Award className="w-4 h-4" /> View
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================
// STUDENT MANAGEMENT
// ============================================================
interface StudentManagementProps {
  storageKey: string;
  externalStudents?: Student[];
  onStudentsChange?: (students: Student[]) => void;
  isModal?: boolean;
}

const StudentManagement = ({
  storageKey,
  externalStudents,
  onStudentsChange,
  isModal = false,
}: StudentManagementProps) => {
  const [students, setStudents] = useState<Student[]>(() => {
    if (externalStudents) return externalStudents;
    const saved = localStorage.getItem(storageKey);
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    if (externalStudents !== undefined) setStudents(externalStudents);
  }, [externalStudents]);

  const saveStudents = (updatedStudents: Student[]) => {
    localStorage.setItem(storageKey, JSON.stringify(updatedStudents));
    setStudents(updatedStudents);
    if (onStudentsChange) onStudentsChange(updatedStudents);
  };

  const deleteStudent = (studentId: string) => {
    if (confirm('Are you sure you want to delete this student?')) {
      const updated = students.filter(s => s.id !== studentId);
      saveStudents(updated);
    }
  };

  if (students.length === 0) {
    return (
      <div className={`text-center py-12 ${isModal ? '' : 'max-w-4xl mx-auto px-4'}`}>
        <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No students registered yet.</p>
        <p className="text-sm text-gray-400">Add students from the main page to track their progress.</p>
      </div>
    );
  }

  return (
    <div className={isModal ? '' : 'max-w-4xl mx-auto px-4'}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 font-semibold text-gray-600">Name</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-600">Grade</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-600">Section</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-600">Email</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-600">Phone</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {students.map((student) => (
              <tr key={student.id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                <td className="py-3 px-4 font-medium text-gray-800">{student.name}</td>
                <td className="py-3 px-4 text-gray-600">{student.grade}</td>
                <td className="py-3 px-4 text-gray-600">{student.section || '-'}</td>
                <td className="py-3 px-4 text-gray-600">{student.email || '-'}</td>
                <td className="py-3 px-4 text-gray-600">{student.phone || '-'}</td>
                <td className="py-3 px-4">
                  <button
                    onClick={() => deleteStudent(student.id)}
                    className="text-red-600 hover:text-red-700 font-medium"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================
// MAIN QUIZ APP COMPONENT
// ============================================================
interface QuizAppProps {
  config: QuizAppData;
}

export default function QuizApp({ config }: QuizAppProps) {
  const data = config.app;
  
  // State
  const [students, setStudents] = useState<Student[]>(() => {
    const saved = localStorage.getItem(`${data.id}_students`);
    return saved ? JSON.parse(saved) : [];
  });

  const [studentQuizHistory, setStudentQuizHistory] = useState<StudentWithGrade[]>(() => {
    const saved = localStorage.getItem(`${data.id}_history`);
    return saved ? JSON.parse(saved) : [];
  });

  const [quizConfig, setQuizConfig] = useState({
    studentName: '',
    started: false,
    selectedTopicId: data.topics[0]?.id || '',
    selectedStudentId: '',
  });

  const [showAddStudent, setShowAddStudent] = useState(false);
  const [viewingCertificate, setViewingCertificate] = useState<StudentWithGrade | null>(null);
  const [newStudent, setNewStudent] = useState({
    name: '',
    grade: '',
    section: '',
    email: '',
    phone: '',
    enrollmentDate: new Date().toISOString().split('T')[0],
  });

  // Memoized values
  const selectedTopic = useMemo(() => 
    data.topics.find(topic => topic.id === quizConfig.selectedTopicId) || data.topics[0],
    [quizConfig.selectedTopicId, data.topics]
  );

  const selectedStudent = useMemo(() => 
    students.find(student => student.id === quizConfig.selectedStudentId),
    [quizConfig.selectedStudentId, students]
  );

  // Handlers
  const saveStudents = useCallback((updatedStudents: Student[]) => {
    localStorage.setItem(`${data.id}_students`, JSON.stringify(updatedStudents));
    setStudents(updatedStudents);
  }, [data.id]);

  const saveHistory = useCallback((updatedHistory: StudentWithGrade[]) => {
    localStorage.setItem(`${data.id}_history`, JSON.stringify(updatedHistory));
    setStudentQuizHistory(updatedHistory);
  }, [data.id]);

  const handleAddStudent = useCallback(() => {
    if (!newStudent.name.trim() || !newStudent.grade.trim()) {
      alert('Please enter at least a name and grade');
      return;
    }

    const student: Student = {
      id: generateId(),
      name: newStudent.name.trim(),
      grade: newStudent.grade.trim(),
      section: newStudent.section.trim() || undefined,
      email: newStudent.email.trim() || undefined,
      phone: newStudent.phone.trim() || undefined,
      enrollmentDate: newStudent.enrollmentDate,
    };

    const updatedStudents = [...students, student];
    saveStudents(updatedStudents);
    
    setNewStudent({
      name: '',
      grade: '',
      section: '',
      email: '',
      phone: '',
      enrollmentDate: new Date().toISOString().split('T')[0],
    });
    setShowAddStudent(false);
    
    setQuizConfig(prev => ({
      ...prev,
      selectedStudentId: student.id,
      studentName: student.name
    }));
  }, [newStudent, students, saveStudents]);

  const handleStudentSelect = useCallback((studentId: string) => {
    const student = students.find(s => s.id === studentId);
    if (student) {
      setQuizConfig(prev => ({
        ...prev,
        selectedStudentId: studentId,
        studentName: student.name
      }));
    }
  }, [students]);

  const handleStudentsChange = useCallback((newStudents: Student[]) => {
    saveStudents(newStudents);
    if (quizConfig.selectedStudentId) {
      const stillExists = newStudents.some(s => s.id === quizConfig.selectedStudentId);
      if (!stillExists) {
        setQuizConfig(prev => ({ ...prev, selectedStudentId: '', studentName: '' }));
      }
    }
  }, [saveStudents, quizConfig.selectedStudentId]);

  const getStudentGrade = useCallback((studentId: string, topicId: string) => {
    return studentQuizHistory.find(
      h => h.studentId === studentId && h.topicId === topicId
    );
  }, [studentQuizHistory]);

  const getStudentGrades = useCallback((studentId: string) => {
    return studentQuizHistory.filter(h => h.studentId === studentId);
  }, [studentQuizHistory]);

  const saveQuizResult = useCallback((result: QuizResult, studentId: string, topicId: string) => {
    const newRecord: StudentWithGrade = {
      studentId,
      studentName: selectedStudent?.name || 'Unknown',
      grade: selectedStudent?.grade || 'N/A',
      topicId,
      score: result.score,
      passed: result.passed,
      completedAt: new Date().toISOString(),
      correctAnswers: result.correctAnswers || 0,
      totalQuestions: result.totalQuestions || 0,
    };

    const filteredHistory = studentQuizHistory.filter(
      h => !(h.studentId === studentId && h.topicId === topicId)
    );
    const updatedHistory = [...filteredHistory, newRecord];
    saveHistory(updatedHistory);
  }, [selectedStudent, studentQuizHistory, saveHistory]);

  const handleQuizComplete = useCallback((result: QuizResult) => {
    if (quizConfig.selectedStudentId) {
      saveQuizResult(result, quizConfig.selectedStudentId, quizConfig.selectedTopicId);
    }
  }, [quizConfig.selectedStudentId, quizConfig.selectedTopicId, saveQuizResult]);

  const handleViewCertificate = useCallback((record: StudentWithGrade) => {
    setViewingCertificate(record);
  }, []);

  const handleChangeTopic = useCallback(() => {
    setQuizConfig(prev => ({ ...prev, started: false }));
  }, []);

  // Get certificate data
  const certificateData = useMemo(() => {
    if (!viewingCertificate) return null;
    const topic = data.topics.find(t => t.id === viewingCertificate.topicId);
    if (!topic) return null;
    return {
      ...viewingCertificate,
      topic,
    };
  }, [viewingCertificate, data.topics]);

  // Render Quiz
  if (quizConfig.started) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <QuizNavbar
          selectedTopic={selectedTopic}
          studentName={quizConfig.studentName}
          onChangeTopic={handleChangeTopic}
        />
        <div className="py-6">
          <Quiz
            title={selectedTopic.title}
            description={selectedTopic.description}
            studentName={quizConfig.studentName}
            questions={selectedTopic.questions}
            passingPercentage={selectedTopic.passingPercentage}
            primaryColor={selectedTopic.color}
            showExplanation={true}
            onComplete={handleQuizComplete}
          />
        </div>
      </div>
    );
  }

  // Render Home Page
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <HeroSection config={data} />
      
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12">
        {/* Student Selector */}
        <MyBox padding="lg" shadow="md" className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-indigo-600" />
              <h2 className="text-lg font-semibold text-gray-800">Student Selection</h2>
            </div>
            <button
              onClick={() => setShowAddStudent(!showAddStudent)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition text-sm font-medium"
            >
              <UserPlus className="w-4 h-4" />
              Add Student
            </button>
          </div>

          {/* Add Student Form */}
          {showAddStudent && (
            <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-md font-semibold text-gray-800">Add New Student</h3>
                <button
                  onClick={() => setShowAddStudent(false)}
                  className="p-1 hover:bg-gray-200 rounded-lg transition"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Student Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={newStudent.name}
                      onChange={(e) => setNewStudent(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter full name"
                      className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Grade *</label>
                  <div className="relative">
                    <GraduationCap className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={newStudent.grade}
                      onChange={(e) => setNewStudent(prev => ({ ...prev, grade: e.target.value }))}
                      placeholder="e.g., 5-A, 6-B"
                      className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Section (Optional)</label>
                  <input
                    type="text"
                    value={newStudent.section}
                    onChange={(e) => setNewStudent(prev => ({ ...prev, section: e.target.value }))}
                    placeholder="e.g., A, B"
                    className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition outline-none"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Email (Optional)</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="email"
                      value={newStudent.email}
                      onChange={(e) => setNewStudent(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="student@email.com"
                      className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Phone (Optional)</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="tel"
                      value={newStudent.phone}
                      onChange={(e) => setNewStudent(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+971 50 123 4567"
                      className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Enrollment Date</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="date"
                      value={newStudent.enrollmentDate}
                      onChange={(e) => setNewStudent(prev => ({ ...prev, enrollmentDate: e.target.value }))}
                      className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition outline-none"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => setShowAddStudent(false)}
                  className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddStudent}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Student
                </button>
              </div>
            </div>
          )}

          {/* Student Select Dropdown */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <select
                  value={quizConfig.selectedStudentId}
                  onChange={(e) => handleStudentSelect(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition outline-none text-gray-800 appearance-none bg-white"
                >
                  <option value="">Select a student...</option>
                  {students.map((student) => (
                    <option key={student.id} value={student.id}>
                      {student.name} - {student.grade}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
            
            {selectedStudent && (
              <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-xl">
                <GraduationCap className="w-4 h-4" />
                <span>Grade: <strong className="text-gray-800">{selectedStudent.grade}</strong></span>
                {selectedStudent.section && (
                  <span className="ml-1">Section: <strong className="text-gray-800">{selectedStudent.section}</strong></span>
                )}
              </div>
            )}
          </div>

          {/* Student Grades Overview */}
          {selectedStudent && studentQuizHistory.length > 0 && (
            <div className="mt-6 border-t border-gray-100 pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Quiz History & Grades</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {getStudentGrades(selectedStudent.id).map((record) => {
                  const topic = data.topics.find(t => t.id === record.topicId);
                  return (
                    <div key={`${record.studentId}-${record.topicId}`} className="flex flex-col bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-800">{topic?.title || 'Unknown Topic'}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                              record.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {record.passed ? '✓ Passed' : '✗ Failed'}
                            </span>
                            <span className="text-xs text-gray-500">Score: {record.score}%</span>
                          </div>
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(record.completedAt).toLocaleDateString()}
                        </span>
                      </div>
                      {record.passed && (
                        <div className="mt-2 flex justify-end">
                          <button
                            onClick={() => handleViewCertificate(record)}
                            className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 px-3 py-1.5 rounded-lg transition"
                          >
                            <Award className="w-3.5 h-3.5" />
                            View Certificate
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </MyBox>

        {/* Topic Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-7">
          {data.topics.map((topic) => {
            const studentGrade = quizConfig.selectedStudentId 
              ? getStudentGrade(quizConfig.selectedStudentId, topic.id)
              : null;
            
            return (
              <TopicCard
                key={topic.id}
                topic={topic}
                isSelected={quizConfig.selectedTopicId === topic.id}
                onSelect={() => setQuizConfig(prev => ({ ...prev, selectedTopicId: topic.id }))}
                onStart={() => {
                  if (!quizConfig.selectedStudentId && !quizConfig.studentName) {
                    alert('Please select a student first');
                    return;
                  }
                  setQuizConfig(prev => ({ ...prev, started: true }));
                }}
                studentGrade={studentGrade}
                onViewCertificate={studentGrade?.passed ? () => handleViewCertificate(studentGrade) : undefined}
              />
            );
          })}
        </div>
      </div>

      {/* Footer Actions */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-12">
        <MyFlexWrapBox gap="md">
          <MyDialogButton
            label="📊 Quiz History"
            dialogHeader="Quiz History"
            fullScreen
            dialogContent={
              <div className="p-6">
                <QuizHistory
                  topics={data.topics}
                  students={students}
                  externalHistory={studentQuizHistory}
                  onViewCertificate={handleViewCertificate}
                />
              </div>
            }
          />

          <MyDialogButton
            label="👥 Students"
            dialogHeader="Student Management"
            fullScreen
            dialogContent={
              <div className="p-6">
                <StudentManagement
                  storageKey={`${data.id}_students`}
                  externalStudents={students}
                  onStudentsChange={handleStudentsChange}
                  isModal
                />
              </div>
            }
          />
        </MyFlexWrapBox>
      </div>

      {/* Certificate Viewer - FullScreen */}
      {certificateData && (
        <FullScreen
          isOpen={!!viewingCertificate}
          onClose={() => setViewingCertificate(null)}
          fullScreen
          animation="zoom"
        >
          <div className="h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-white p-8">
            <div className="w-full max-w-[420px] transform scale-100 md:scale-110 lg:scale-125">
              <Certificate
                title={certificateData.topic.title}
                studentName={certificateData.studentName}
                percentage={certificateData.score}
                grade={certificateData.grade}
                topicIcon={certificateData.topic.icon}
                certificateId={`CERT-${certificateData.studentId.slice(-4)}-${certificateData.topicId.slice(0, 4)}-${Date.now().toString().slice(-6)}`}
                issueDate={certificateData.completedAt}
                passed={certificateData.passed}
                correctAnswers={certificateData.correctAnswers}
                totalQuestions={certificateData.totalQuestions}
              />
            </div>

            <div className="flex gap-4 mt-8">
              <button
                onClick={() => window.print()}
                className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition flex items-center gap-2 shadow-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print
              </button>
              <button
                onClick={() => setViewingCertificate(null)}
                className="px-6 py-3 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 transition"
              >
                Close
              </button>
            </div>

            <p className="text-xs text-gray-400 mt-4">
              📸 Use Cmd+Shift+4 (Mac) or Win+Shift+S (Windows) to capture
            </p>
          </div>
        </FullScreen>
      )}
    </div>
  );
}